import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Badge, Loading, EmptyState } from '../components/ui';

const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

export default function CostingMaster() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});        // { [id]: { sam, cost_per_sam } } as typed strings
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [bulkRate, setBulkRate] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = 'Costing master';
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/costing/master');
      setRows(data); setEdits({});
    } catch (e) { setMsg('Error: could not load costing master'); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const val = (r, f) => (edits[r.id] && edits[r.id][f] !== undefined) ? edits[r.id][f] : String(r[f]);
  const setField = (r, f, v) =>
    setEdits(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), [f]: v } }));

  const filtered = rows.filter(r =>
    (!search || r.operation_name.toLowerCase().includes(search.toLowerCase()) ||
      (r.machine_type || '').toLowerCase().includes(search.toLowerCase())) &&
    (!filterType || r.op_type === filterType));

  const applyBulk = () => {
    if (bulkRate === '' || num(bulkRate) < 0) return;
    if (!window.confirm(`Set cost/SAM = ${bulkRate} for ${filtered.length} operation(s) shown?`)) return;
    setEdits(prev => {
      const next = { ...prev };
      filtered.forEach(r => { next[r.id] = { ...(next[r.id] || {}), cost_per_sam: String(bulkRate) }; });
      return next;
    });
  };

  const dirtyIds = Object.keys(edits);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const payload = dirtyIds.map(id => {
        const r = rows.find(x => String(x.id) === String(id));
        return { id: r.id, sam: val(r, 'sam'), cost_per_sam: val(r, 'cost_per_sam') };
      });
      await api.put('/costing/master', { rows: payload });
      setMsg(`✓ Saved ${payload.length} operation(s)`);
      await fetchAll();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
  };

  const notSet = rows.filter(r => num(r.cost_per_sam) === 0).length;

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="inp" placeholder="Search operation or machine…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width: 220 }} />
        <select className="inp" value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: 130 }}>
          <option value="">All types</option>
          <option value="Machine">Machine</option>
          <option value="Non-machine">Non-machine</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
          <input className="inp" type="number" min="0" step="0.0001" placeholder="Cost/SAM"
            value={bulkRate} onChange={e => setBulkRate(e.target.value)} style={{ width: 100 }} />
          <Btn size="sm" onClick={applyBulk} disabled={bulkRate === '' || !filtered.length}>
            Apply to {filtered.length} shown
          </Btn>
        </div>
        <div style={{ flex: 1 }} />
        {notSet > 0 && <Badge type="amber">{notSet} without cost/SAM</Badge>}
        <Btn size="sm" variant="primary" onClick={save} disabled={!dirtyIds.length || saving}>
          {saving ? 'Saving…' : `Save changes${dirtyIds.length ? ` (${dirtyIds.length})` : ''}`}
        </Btn>
      </div>

      {msg && (
        <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 6, marginBottom: 10,
          background: msg.startsWith('✓') ? 'var(--grn-bg)' : 'var(--red-bg)',
          color: msg.startsWith('✓') ? 'var(--grn)' : 'var(--red)' }}>{msg}</div>
      )}

      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
        A copy of the Operation Master used only for costing: one row per operation and machine type, each with its own SAM and cost/SAM.
        Changing SAM here does not affect line design or balancing. New operations and machine types added in the Operation Master appear here automatically with cost/SAM 0.
      </div>

      {loading ? <Loading /> : filtered.length === 0 ? <EmptyState message="No operations found" /> : (
        <div style={{ background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden' }}>
          <table className="lc-table">
            <thead>
              <tr>
                <th>Operation</th><th>Machine type</th>
                <th style={{ width: 110 }}>SAM (min)</th>
                <th style={{ width: 120 }}>Cost / SAM</th>
                <th style={{ width: 110, textAlign: 'right' }}>Cost / op</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const changed = !!edits[r.id];
                return (
                  <tr key={r.id} style={changed ? { background: 'var(--amb-bg)' } : undefined}>
                    <td style={{ fontWeight: 500 }}>{r.operation_name}</td>
                    <td>
                      {r.machine_type
                        ? <Badge type="blue">{r.machine_type}</Badge>
                        : <Badge type="gray">{r.op_type === 'Non-machine' ? 'Non-machine' : 'No machine set'}</Badge>}
                    </td>
                    <td><input className="inp" type="number" min="0" step="0.001" value={val(r, 'sam')}
                      onChange={e => setField(r, 'sam', e.target.value)} style={{ width: 90 }} /></td>
                    <td><input className="inp" type="number" min="0" step="0.0001" value={val(r, 'cost_per_sam')}
                      onChange={e => setField(r, 'cost_per_sam', e.target.value)} style={{ width: 100 }} /></td>
                    <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--acc)' }}>
                      {(num(val(r, 'sam')) * num(val(r, 'cost_per_sam'))).toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}